/**
 * Settings › Templates: add a stream from any template to an organisation
 * that already exists, after a preview of exactly what it adds.
 *
 * The same definitions the sign-up question uses (`routes/v1/templates.rs`),
 * except that anything already there (a pipeline, return reasons) is kept
 * as it is, which the server enforces. A template already added says so and adds
 * nothing again.
 */
import { useState } from 'react';
import {
  Alert, Anchor, Badge, Button, Card, Group, List, Loader, Modal, SimpleGrid, Stack, Text,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconCheck } from '@tabler/icons-react';
import { Link } from 'react-router-dom';
import {
  useApplyTemplate, useTemplatePreview, useTemplates, type TemplateSummary,
} from '../../api/templates';
import { useVocab } from '../../vocab';

function PreviewModal({ template, onClose }: { template: TemplateSummary; onClose: () => void }) {
  const v = useVocab();
  const preview = useTemplatePreview(template.key, false);
  const apply = useApplyTemplate();
  const p = preview.data;

  const add = () =>
    apply.mutate(
      { key: template.key, onboarding: false },
      {
        onSuccess: (r) => {
          notifications.show({
            color: 'teal',
            title: r.applied ? `${template.name} added` : 'Nothing to add',
            message: r.applied
              ? `${r.created.length} ${r.created.length === 1 ? 'thing' : 'things'} added.${r.next_step ? ` ${r.next_step}` : ''}`
              : 'This template was added before.',
          });
          onClose();
        },
        onError: (e) =>
          notifications.show({ color: 'red', title: 'Could not add it', message: (e as Error).message }),
      },
    );

  return (
    <Modal opened onClose={onClose} size="lg" centered title={<Text fw={700}>{`Add from ${template.name}`}</Text>}>
      <Stack gap="md">
        {preview.isPending && <Loader size="sm" />}
        {preview.isError && (
          <Alert color="red" variant="light">{(preview.error as Error).message}</Alert>
        )}
        {p && p.already_applied && (
          <Alert color="gray" variant="light" icon={<IconCheck size={16} />}>
            {`Already added. ${p.keeps.join('. ')}.`}
          </Alert>
        )}
        {p && !p.already_applied && (
          <>
            <div data-testid="template-adds">
              <Text size="sm" fw={600} mb={4}>This will add</Text>
              <List size="sm" spacing={4}>
                {p.adds.map((a) => <List.Item key={a}>{a}</List.Item>)}
              </List>
            </div>
            {p.keeps.length > 0 && (
              <div data-testid="template-keeps">
                <Text size="sm" fw={600} mb={4}>Kept as it is</Text>
                <List size="sm" spacing={4} c="dimmed">
                  {p.keeps.map((k) => <List.Item key={k}>{k}</List.Item>)}
                </List>
              </div>
            )}
            <Text size="xs" c="dimmed">
              {`Nothing that is already here changes, and nothing is sent anywhere: a template never makes up ${v.many('buyer')} or ${v.many('destination')}.`}
            </Text>
            {p.next_step && <Text size="xs" c="dimmed">{p.next_step}</Text>}
          </>
        )}
        <Group justify="flex-end">
          <Button variant="subtle" color="gray" onClick={onClose} disabled={apply.isPending}>
            {p?.already_applied ? 'Close' : 'Cancel'}
          </Button>
          {p && !p.already_applied && (
            <Button onClick={add} loading={apply.isPending}>
              {`Add ${template.name}`}
            </Button>
          )}
        </Group>
      </Stack>
    </Modal>
  );
}

export function TemplatesTab() {
  const v = useVocab();
  const templates = useTemplates(true);
  const [open, setOpen] = useState<TemplateSummary | null>(null);

  if (templates.isPending) return <Loader size="sm" />;
  if (templates.isError) {
    return <Alert color="red" variant="light">{(templates.error as Error).message}</Alert>;
  }

  return (
    <Stack>
      <div>
        <Text fw={600}>Templates</Text>
        <Text size="sm" c="dimmed">
          {`Add ${v.a('campaign')} set up for something else, with what it needs. You see exactly what it adds before anything is made.`}
        </Text>
      </div>
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
        {templates.data.templates.map((t) => (
          <Card key={t.key} withBorder radius="md" padding="lg" data-testid={`template-${t.key}`}>
            <Stack gap="sm" h="100%">
              <Group justify="space-between" align="flex-start" wrap="nowrap">
                <div>
                  <Text fw={600}>{t.name}</Text>
                  <Text size="sm" c="dimmed">{t.description}</Text>
                </div>
                {t.applied && (
                  <Badge variant="light" color="chartGreen" tt="none" style={{ flexShrink: 0 }}>Added</Badge>
                )}
              </Group>
              <Group justify="space-between" mt="auto">
                {t.applied && t.applied_campaign_id ? (
                  <Anchor
                    component={Link}
                    to={`/campaigns/${encodeURIComponent(t.applied_campaign_id)}`}
                    size="sm"
                    c="inherit"
                    underline="always"
                  >
                    {`Open the ${v.one('campaign')}`}
                  </Anchor>
                ) : <span />}
                <Button variant="default" size="xs" onClick={() => setOpen(t)}>
                  {t.applied ? 'Preview' : 'Preview and add'}
                </Button>
              </Group>
            </Stack>
          </Card>
        ))}
      </SimpleGrid>
      {open && <PreviewModal template={open} onClose={() => setOpen(null)} />}
    </Stack>
  );
}
